import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, IconButton, Typography, CircularProgress, Slider, Tooltip, Zoom, Menu, MenuItem } from '@mui/material';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward10Icon from '@mui/icons-material/Forward10';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import WaveSurfer from 'wavesurfer.js';

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Zoom ref={ref} {...props} />;
});

const useStyles = makeStyles(() => ({
	'@keyframes eq': {
		'0%, 100%': { height: '25%' },
		'50%': { height: '100%' }
	},
	'@keyframes ripple': {
		'0%': { transform: 'scale(0.85)', opacity: 0.55 },
		'100%': { transform: 'scale(1.9)', opacity: 0 }
	},
	eqBadge: {
		width: 44,
		height: 44,
		borderRadius: 12,
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'center',
		gap: 3,
		padding: '0 11px 12px',
		backgroundColor: 'rgba(255, 255, 255, 0.16)',
		boxSizing: 'border-box',
		flexShrink: 0
	},
	eqBar: {
		width: 3,
		height: '35%',
		borderRadius: 3,
		backgroundColor: 'rgba(255, 255, 255, 0.95)',
		transition: 'height 0.2s ease'
	},
	eqBarPlaying: {
		animationName: '$eq',
		animationDuration: '0.9s',
		animationTimingFunction: 'ease-in-out',
		animationIterationCount: 'infinite'
	},
	ripple: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		borderRadius: '50%',
		animationName: '$ripple',
		animationDuration: '1.8s',
		animationTimingFunction: 'ease-out',
		animationIterationCount: 'infinite',
		pointerEvents: 'none'
	}
}));

const AudioPlayerModal = ({ open, onClose, audioUrl, callId, onDownload }) => {
	const theme = useTheme();
	const classes = useStyles();
	const waveformRef = useRef(null);
	const wavesurferRef = useRef(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);
	const [volume, setVolume] = useState(1);
	const [isMuted, setIsMuted] = useState(false);
	const [playbackRate, setPlaybackRate] = useState(1);
	const [rateAnchorEl, setRateAnchorEl] = useState(null);

	// Theme-aware colors
	const isDarkMode = theme.palette.type === 'dark';
	const accent = theme.palette.background.tableHeader;
	const waveColor = isDarkMode ? '#3d444d' : '#c3cfdb';
	const progressColor = isDarkMode ? '#4a90d9' : accent;
	const headerGradient = 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 55%), #000000';

	useEffect(() => {
		if (!open || !audioUrl) return undefined;

		setIsLoading(true);
		setLoadError(null);
		setIsPlaying(false);
		setCurrentTime(0);
		setDuration(0);

		// Clean up previous instance
		if (wavesurferRef.current) {
			wavesurferRef.current.destroy();
			wavesurferRef.current = null;
		}

		const initWaveSurfer = async () => {
			// Wait for the DOM element to be available
			const waitForRef = () => {
				return new Promise((resolve) => {
					const checkRef = () => {
						if (waveformRef.current) {
							resolve();
						} else {
							requestAnimationFrame(checkRef);
						}
					};
					checkRef();
				});
			};

			try {
				await waitForRef();

				// Fetch audio as blob to avoid CORS issues
				const response = await fetch(audioUrl);
				if (!response.ok) {
					throw new Error(`Failed to fetch audio: ${response.status}`);
				}
				const blob = await response.blob();
				const blobUrl = URL.createObjectURL(blob);

				// Double check ref is still valid and modal is still open
				if (!waveformRef.current) {
					URL.revokeObjectURL(blobUrl);
					return;
				}

				wavesurferRef.current = WaveSurfer.create({
					container: waveformRef.current,
					waveColor,
					progressColor,
					cursorColor: progressColor,
					cursorWidth: 2,
					barWidth: 3,
					barRadius: 3,
					barGap: 2,
					height: 88,
					normalize: true
				});

				wavesurferRef.current.load(blobUrl);

				wavesurferRef.current.on('ready', () => {
					setIsLoading(false);
					setDuration(wavesurferRef.current.getDuration());
					wavesurferRef.current.setVolume(isMuted ? 0 : volume);
					wavesurferRef.current.setPlaybackRate(playbackRate);
				});

				wavesurferRef.current.on('timeupdate', (time) => {
					setCurrentTime(time);
				});

				wavesurferRef.current.on('play', () => setIsPlaying(true));
				wavesurferRef.current.on('pause', () => setIsPlaying(false));
				wavesurferRef.current.on('finish', () => setIsPlaying(false));

				wavesurferRef.current.on('error', (err) => {
					console.error('WaveSurfer error:', err);
					setLoadError('Unable to load the recording waveform.');
					setIsLoading(false);
				});
			} catch (err) {
				console.error('Failed to load audio:', err);
				setLoadError(err.message || 'Failed to load the recording.');
				setIsLoading(false);
			}
		};

		initWaveSurfer();

		return () => {
			if (wavesurferRef.current) {
				wavesurferRef.current.destroy();
				wavesurferRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, audioUrl, waveColor, progressColor]);

	// Keep volume / mute in sync with the player
	useEffect(() => {
		if (wavesurferRef.current) {
			wavesurferRef.current.setVolume(isMuted ? 0 : volume);
		}
	}, [volume, isMuted]);

	// Keep playback rate in sync with the player
	useEffect(() => {
		if (wavesurferRef.current) {
			wavesurferRef.current.setPlaybackRate(playbackRate);
		}
	}, [playbackRate]);

	const handlePlayPause = () => {
		if (wavesurferRef.current) {
			wavesurferRef.current.playPause();
		}
	};

	const handleSkip = (seconds) => {
		const ws = wavesurferRef.current;
		if (!ws) return;
		const total = ws.getDuration() || 0;
		const next = Math.min(Math.max(ws.getCurrentTime() + seconds, 0), total);
		ws.setTime(next);
		setCurrentTime(next);
	};

	const handleMuteToggle = () => setIsMuted((prev) => !prev);

	const handleVolumeChange = (_event, newValue) => {
		const vol = newValue / 100;
		setVolume(vol);
		if (vol > 0 && isMuted) setIsMuted(false);
		if (vol === 0) setIsMuted(true);
	};

	const handleRateSelect = (rate) => {
		setPlaybackRate(rate);
		setRateAnchorEl(null);
	};

	const formatTime = (seconds) => {
		if (!seconds || Number.isNaN(seconds)) return '0:00';
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins}:${secs.toString().padStart(2, '0')}`;
	};

	const handleClose = () => {
		if (wavesurferRef.current) {
			wavesurferRef.current.pause();
		}
		onClose();
	};

	const volumeIsOff = isMuted || volume === 0;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			TransitionComponent={Transition}
			PaperProps={{
				style: {
					borderRadius: '20px',
					backgroundColor: theme.palette.background.paper,
					border: `1px solid ${theme.palette.divider}`,
					overflow: 'hidden',
					boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)'
				}
			}}
		>
			{/* Header */}
			<div
				style={{
					background: headerGradient,
					padding: '18px 22px',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center'
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
					<div className={classes.eqBadge}>
						{[0, 1, 2, 3].map((i) => (
							<span
								key={i}
								className={`${classes.eqBar} ${isPlaying ? classes.eqBarPlaying : ''}`}
								style={{
									height: isPlaying ? undefined : `${[45, 80, 55, 35][i]}%`,
									animationDelay: `${i * 0.15}s`
								}}
							/>
						))}
					</div>
					<div style={{ minWidth: 0 }}>
						<Typography
							variant="h6"
							style={{
								fontWeight: 600,
								color: '#FFFFFF',
								fontSize: '1.7rem',
								lineHeight: 1.2
							}}
						>
							Call Recording
						</Typography>
						{callId && (
							<Typography
								noWrap
								style={{
									color: 'rgba(255, 255, 255, 0.72)',
									fontSize: '1.2rem',
									marginTop: '2px'
								}}
							>
								Call ID: {callId}
							</Typography>
						)}
					</div>
				</div>
				<IconButton
					onClick={handleClose}
					size="small"
					style={{
						color: '#FFFFFF',
						backgroundColor: 'rgba(255, 255, 255, 0.12)'
					}}
				>
					<CloseIcon />
				</IconButton>
			</div>

			<DialogContent
				style={{
					backgroundColor: theme.palette.background.paper,
					padding: '24px 22px 22px'
				}}
			>
				{/* Error state */}
				{loadError && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
							padding: '18px',
							textAlign: 'left',
							backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)',
							borderRadius: '12px',
							border: `1px solid ${isDarkMode ? 'rgba(244, 67, 54, 0.35)' : 'rgba(244, 67, 54, 0.2)'}`
						}}
					>
						<ErrorOutlineIcon style={{ color: '#f44336', fontSize: '28px' }} />
						<Typography style={{ color: '#f44336', fontWeight: 500, fontSize: '1.4rem' }}>
							{loadError}
						</Typography>
					</div>
				)}

				{/* Waveform container */}
				{!loadError && (
					<div
						style={{
							position: 'relative',
							minHeight: '88px',
							padding: '16px 18px',
							marginBottom: '18px',
							borderRadius: '14px',
							backgroundColor: theme.palette.background.default,
							border: `1px solid ${theme.palette.divider}`
						}}
					>
						{isLoading && (
							<div
								style={{
									position: 'absolute',
									inset: 0,
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									gap: '12px',
									backgroundColor: theme.palette.background.default,
									borderRadius: '14px',
									zIndex: 1
								}}
							>
								<CircularProgress size={26} style={{ color: progressColor }} />
								<Typography style={{ color: theme.palette.text.secondary, fontSize: '1.4rem' }}>
									Loading audio…
								</Typography>
							</div>
						)}
						<div
							ref={waveformRef}
							style={{
								width: '100%',
								minHeight: '88px',
								opacity: isLoading ? 0 : 1,
								transition: 'opacity 0.25s ease'
							}}
						/>
					</div>
				)}

				{!loadError && (
					<>
						{/* Time row */}
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								marginBottom: '10px',
								padding: '0 2px'
							}}
						>
							<Typography
								style={{
									fontWeight: 600,
									color: theme.palette.text.primary,
									fontSize: '1.4rem',
									fontVariantNumeric: 'tabular-nums'
								}}
							>
								{formatTime(currentTime)}
							</Typography>
							<Typography
								style={{
									color: theme.palette.text.secondary,
									fontSize: '1.4rem',
									fontVariantNumeric: 'tabular-nums'
								}}
							>
								{formatTime(duration)}
							</Typography>
						</div>

						{/* Main transport controls */}
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: '20px',
								marginBottom: '20px'
							}}
						>
							<Tooltip title="Back 10s" arrow>
								<span>
									<IconButton
										onClick={() => handleSkip(-10)}
										disabled={isLoading}
										style={{
											color: theme.palette.text.primary,
											width: '44px',
											height: '44px',
											opacity: isLoading ? 0.4 : 1
										}}
									>
										<Replay10Icon style={{ fontSize: '28px' }} />
									</IconButton>
								</span>
							</Tooltip>

							<div style={{ position: 'relative', width: '64px', height: '64px' }}>
								{isPlaying && (
									<span className={classes.ripple} style={{ border: `2px solid ${progressColor}` }} />
								)}
								<IconButton
									onClick={handlePlayPause}
									disabled={isLoading}
									style={{
										position: 'relative',
										background: 'linear-gradient(135deg, #2a2a2a 0%, #000000 100%)',
										color: '#FFFFFF',
										width: '64px',
										height: '64px',
										boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
										opacity: isLoading ? 0.5 : 1
									}}
								>
									{isPlaying ? (
										<PauseIcon style={{ fontSize: '34px' }} />
									) : (
										<PlayArrowIcon style={{ fontSize: '34px', marginLeft: '2px' }} />
									)}
								</IconButton>
							</div>

							<Tooltip title="Forward 10s" arrow>
								<span>
									<IconButton
										onClick={() => handleSkip(10)}
										disabled={isLoading}
										style={{
											color: theme.palette.text.primary,
											width: '44px',
											height: '44px',
											opacity: isLoading ? 0.4 : 1
										}}
									>
										<Forward10Icon style={{ fontSize: '28px' }} />
									</IconButton>
								</span>
							</Tooltip>
						</div>

						{/* Bottom toolbar: speed / volume / download */}
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '12px',
								paddingTop: '16px',
								borderTop: `1px solid ${theme.palette.divider}`
							}}
						>
							<Tooltip title="Playback speed" arrow>
								<span>
									<button
										type="button"
										onClick={(e) => setRateAnchorEl(e.currentTarget)}
										disabled={isLoading}
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '2px',
											minWidth: '64px',
											height: '34px',
											padding: '0 6px 0 12px',
											borderRadius: '17px',
											border: `1px solid ${rateAnchorEl ? progressColor : theme.palette.divider}`,
											backgroundColor: 'transparent',
											color: theme.palette.text.primary,
											fontWeight: 600,
											fontSize: '1.3rem',
											cursor: isLoading ? 'default' : 'pointer',
											opacity: isLoading ? 0.4 : 1
										}}
									>
										{playbackRate}x
										<ArrowDropDownIcon
											style={{
												fontSize: '20px',
												transition: 'transform 0.2s ease',
												transform: rateAnchorEl ? 'rotate(180deg)' : 'none'
											}}
										/>
									</button>
								</span>
							</Tooltip>
							<Menu
								anchorEl={rateAnchorEl}
								open={Boolean(rateAnchorEl)}
								onClose={() => setRateAnchorEl(null)}
								anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
								transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
								PaperProps={{
									style: {
										borderRadius: '12px',
										backgroundColor: theme.palette.background.paper,
										border: `1px solid ${theme.palette.divider}`,
										boxShadow: '0 8px 24px rgba(0, 0, 0, 0.22)',
										minWidth: '96px'
									}
								}}
							>
								{PLAYBACK_RATES.map((rate) => {
									const selected = rate === playbackRate;
									return (
										<MenuItem
											key={rate}
											selected={selected}
											onClick={() => handleRateSelect(rate)}
											style={{
												fontSize: '1.3rem',
												fontWeight: selected ? 700 : 500,
												color: selected ? progressColor : theme.palette.text.primary,
												justifyContent: 'center'
											}}
										>
											{rate === 1 ? 'Normal' : `${rate}x`}
										</MenuItem>
									);
								})}
							</Menu>

							<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, maxWidth: '160px' }}>
								<IconButton
									onClick={handleMuteToggle}
									disabled={isLoading}
									size="small"
									style={{ color: theme.palette.text.primary, opacity: isLoading ? 0.4 : 1 }}
								>
									{volumeIsOff ? (
										<VolumeOffIcon style={{ fontSize: '22px' }} />
									) : (
										<VolumeUpIcon style={{ fontSize: '22px' }} />
									)}
								</IconButton>
								<Slider
									value={Math.round((volumeIsOff ? 0 : volume) * 100)}
									onChange={handleVolumeChange}
									disabled={isLoading}
									aria-label="Volume"
									size="small"
									sx={{
										color: progressColor,
										'& .MuiSlider-thumb': {
											width: 12,
											height: 12
										}
									}}
								/>
							</div>

							<Tooltip title="Download recording" arrow>
								<span>
									<IconButton
										onClick={() => onDownload(audioUrl, callId)}
										disabled={isLoading}
										style={{
											color: progressColor,
											width: '42px',
											height: '42px',
											border: `1.5px solid ${progressColor}`,
											opacity: isLoading ? 0.4 : 1
										}}
									>
										<DownloadIcon style={{ fontSize: '22px' }} />
									</IconButton>
								</span>
							</Tooltip>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default AudioPlayerModal;
